/*
 * Test-Tour (AAR-1993) — nur Entwicklung und Cypress, nie für Benutzer.
 *
 * Prüft den Tour-Kern ohne fachliche Tour: langer Text bei geringer Höhe,
 * Anker oben, Scroll-Ziel weit unten, langsame Vorbereitung, die ein Menü
 * öffnet (Abbruch mittendrin), Seitenwechsel, optional fehlender Anker und
 * Gate-Fehler. Ergebnis und Protokoll landen in `window.__aaremoTourRuns`.
 *
 *   window.__aaremoTestTour({ slowMs: 1500, missing: true, gateError: true })
 *   window.__aaremoStopTour('band' | 'external' | 'logout')
 */
import { isCypress } from '@/helpers/isCypress'
import { bandFor, type TourLogEntry } from '@/helpers/tourBand'
import { router } from '@/plugins/router'
import {
	beginTourRun,
	filterSteps,
	findAnchor,
	runTour,
	stopTour,
	tourAnchor,
	tourWindowWidth,
	type GateContext,
	type TourEndReason,
	type TourStepDefinition,
} from '@/composables/useTour'

/** Tour-Kennungen des Anpassen-Knopfs und seiner Menüliste. */
const SETTINGS_BTN = 'dashboardSettings'
const SETTINGS_MENU = 'dashboardSettingsMenu'

/** Die Menüliste hängt nur im DOM, solange das Menü offen ist. */
const menuOpen = () => !!document.querySelector(tourAnchor(SETTINGS_MENU))
const clickSettings = () =>
	document.querySelector<HTMLElement>(tourAnchor(SETTINGS_BTN))?.click()

// Test-Werkzeug: dieselben Kanten wie Vuetifys sm/md-Default.
const BANDS = [
	{ band: 'narrow', min: 0 },
	{ band: 'medium', min: 600 },
	{ band: 'wide', min: 960 },
] as const
const bandNow = () => bandFor(tourWindowWidth().value, BANDS)

const LONG_TEXT = Array.from(
	{ length: 6 },
	(_, i) =>
		`Absatz ${i + 1}: Dieser Text ist absichtlich lang, damit das Fenster bei geringer Höhe nur im Text scrollt und Titel sowie Knöpfe sichtbar bleiben.`
).join('<br><br>')

let menuOpenedByTour = false

/** Schließt das Anpassen-Menü nur, wenn die Tour es geöffnet hat. */
function closeTourMenu() {
	if (!menuOpenedByTour) return
	menuOpenedByTour = false
	if (menuOpen()) clickSettings()
}

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms))

type TestTourOptions = { slowMs?: number; missing?: boolean; gateError?: boolean }

async function startTestTour({
	slowMs = 1500,
	missing = false,
	gateError = false,
}: TestTourOptions = {}): Promise<TourEndReason | null> {
	const run = beginTourRun()
	if (!run) return null
	try {
		if (router.currentRoute.value.name !== 'dashboard') {
			await router.push({ name: 'dashboard' }).catch(() => undefined)
		}
		await findAnchor('dashboardSearch', 5000)
		if (run.isCancelled()) return null

		const steps: TourStepDefinition[] = [
			{ id: 'long', title: 'Test: langer Text', text: LONG_TEXT },
			{
				id: 'search',
				title: 'Test: Anker oben',
				text: 'Die Suche oben auf dem Dashboard.',
				anchor: 'dashboardSearch',
				placement: 'bottom',
			},
			{
				id: 'scroll',
				title: 'Test: Scroll-Ziel',
				text: 'Das letzte Widget im Raster, weit unten.',
				anchor: 'testTourLast',
				placement: 'top',
				// Nur für die Test-Tour: das letzte Raster-Element selbst markieren,
				// statt dafür eine Kennung im Dashboard zu pflegen.
				prepare: () => {
					const items = document.querySelectorAll(tourAnchor('dashboardGridItem'))
					items[items.length - 1]?.setAttribute(
						'data-tour',
						'dashboardGridItem testTourLast'
					)
				},
			},
			{
				id: 'slow',
				title: 'Test: langsame Vorbereitung',
				text: 'Die Vorbereitung wartet und öffnet dann das Anpassen-Menü.',
				anchor: SETTINGS_MENU,
				placement: 'left',
				prepare: async (signal) => {
					await pause(slowMs)
					if (signal.aborted) return
					if (!menuOpen()) {
						clickSettings()
						menuOpenedByTour = true
					}
					await pause(300)
				},
			},
			{
				id: 'route',
				title: 'Test: Seitenwechsel',
				text: 'Das Profil auf einer anderen Seite.',
				anchor: 'profileCard',
				placement: 'bottom',
				route: 'profile',
				prepare: () => closeTourMenu(),
			},
			...(missing
				? [
						{
							id: 'missing',
							title: 'Test: fehlender Anker',
							text: 'Dieser Schritt darf nie erscheinen.',
							anchor: 'gibtEsNicht',
						},
					]
				: []),
			...(gateError
				? [
						{
							id: 'gate-error',
							title: 'Test: Gate-Fehler',
							text: 'Dieser Schritt darf nie erscheinen.',
							gate: () => {
								throw new Error('Test: API nicht erreichbar')
							},
						},
					]
				: []),
			{ id: 'outro', title: 'Test: Ende', text: 'Ende der Test-Tour.' },
		]

		const log: TourLogEntry[] = []
		const band = bandNow()
		const ctx = {
			checkIfSubscribedTo: () => ({ allowed: true }),
			settings: undefined,
		} as unknown as GateContext
		const filtered = await filterSteps(steps, ctx, { band, log, tour: 'test' })
		if (run.isCancelled()) return null

		return await runTour(filtered, {
			seenKey: 'test-tour',
			isCancelled: run.isCancelled,
			band: { current: band, now: bandNow },
			log,
			tour: 'test',
			onEnd: (reason) => {
				if (reason !== 'logout') closeTourMenu()
			},
		})
	} finally {
		run.end()
	}
}

/** Hängt die Test-Tour an `window` — nur in Entwicklung und unter Cypress. */
export function registerTestTour() {
	if (!import.meta.env.DEV && !isCypress()) return
	const w = window as unknown as Record<string, unknown>
	w.__aaremoTestTour = startTestTour
	w.__aaremoStopTour = (reason?: TourEndReason) => stopTour({ reason })
}
